import { StateBlock } from "@/components/layout-2/ds";
import { WhyPane, WhySheet, type Explanation } from "@/components/layout-2/ds-settings";
import { DataService } from "@/services/data";
import { useLoad } from "./use-load";
import type { Why } from "./types";

/** The served "Why?" as the sheet reads it: actions without a link are dropped, relative links are resolved against `base`. */
function toExplanation(why: Why, base: string): Explanation {
    return {
        title: why.title,
        body: why.body,
        tone: why.tone,
        facts: why.facts.map(fact => [fact.label, fact.value]),
        actions: why.actions.flatMap(action => (action.href ? [{ label: action.label, href: `${base}/${action.href}` }] : [])),
        reason: why.reason
    };
}

/**
 * "Why?" of an automatic decision, fetched from `url` when the sheet opens. The same panel serves
 * a table row, a card journal and a record panel; `base` is the URL `actions[].href` are relative to.
 */
export function WhyPanel({ url, base, subject, open, onOpenChange }: { url: string; base: string; subject?: React.ReactNode; open: boolean; onOpenChange: (open: boolean) => void }) {
    const { data, error, loading, reload } = useLoad<Why | null>(() => (open ? DataService.client.url(url).get().json<Why>() : Promise.resolve(null)), [url, open], "Unable to explain this decision.");
    return (
        <WhySheet
            explanation={data ? toExplanation(data, base) : null}
            subject={subject}
            open={open}
            onOpenChange={onOpenChange}
            state={open && <StateBlock kind={loading ? "loading" : "error"} message={error ?? undefined} onRetry={reload} />}
        />
    );
}

/** The same "Why?" as a pane inside a dialog (a pass, the preview): fetched from `url` while it is shown. */
export function WhyInline({ url, base, subject, onClose }: { url: string; base: string; subject?: React.ReactNode; onClose: () => void }) {
    const { data, error, loading, reload } = useLoad<Why>(() => DataService.client.url(url).get().json<Why>(), [url], "Unable to explain this decision.");
    return <WhyPane explanation={data ? toExplanation(data, base) : null} subject={subject} onClose={onClose} state={<StateBlock kind={loading ? "loading" : "error"} message={error ?? undefined} onRetry={reload} />} />;
}
